import { useVerifyEmailMfaMutation, useResendEmailMfaCodeMutation } from '@codezo/graphql';
import { useThemeColors } from '@codezo/theme';
import {
	Alert,
	Anchor,
	Box,
	Button,
	Group,
	Paper,
	PinInput,
	Stack,
	Text,
	Title,
} from '@mantine/core';
import { IconAlertCircle, IconArrowLeft, IconRefresh } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import { Logo } from '../components/common/Logo';
import { handleLoginValidate } from '../utils/login';

export function MFAEmailVerifyPage() {
	const [code, setCode] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [isResending, setIsResending] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();

	const colors = useThemeColors();
	const isMobile = useMediaQuery('(max-width: 48em)');
	const userId = location.state?.userId as string | undefined;
	const email = location.state?.email as string | undefined;

	useEffect(() => {
		if (!userId) {
			navigate('/auth/login');
		}
	}, [userId, navigate]);

	// Email MFA verification
	const [verifyEmailMfa, { loading }] = useVerifyEmailMfaMutation({
		onCompleted: (data) => {
			if (data.verifyEmailMfa.token && data.verifyEmailMfa.role) {
				// Use role from the response
				handleLoginValidate(data.verifyEmailMfa.token, data.verifyEmailMfa.role);
			}
		},
		onError: (err) => {
			setError(err.message || 'Invalid verification code. Please try again.');
		},
	});

	// Resend email code mutation
	const [resendEmailMfaCode] = useResendEmailMfaCodeMutation();

	const handleVerify = async () => {
		if (!userId || code.length !== 6) return;

		setError(null);

		try {
			await verifyEmailMfa({
				variables: {
					input: {
						userId,
						code,
					},
				},
			});
		} catch {
			// Error is already handled by onError callback
			// Error handled by mutation onError
		}
	};

	const handleResendCode = async () => {
		if (!userId) return;

		setIsResending(true);
		try {
			const { data: resendData } = await resendEmailMfaCode({
				variables: {
					userId,
				},
			});

			if (resendData?.resendEmailMfaCode?.success) {
				notifications.show({
					title: 'Code Sent',
					message: email
						? `A new verification code has been sent to ${email}`
						: 'A new verification code has been sent to your email',
					color: 'green',
				});
			} else {
				notifications.show({
					title: 'Failed to Send',
					message:
						resendData?.resendEmailMfaCode?.message ||
						'Could not send a new code. Please try again.',
					color: 'red',
				});
			}
		} catch {
			// Error handled by mutation onError
			notifications.show({
				title: 'Failed to Send',
				message: 'Could not send a new code. Please try again.',
				color: 'red',
			});
		} finally {
			setTimeout(() => setIsResending(false), 3000); // Prevent spam
		}
	};

	return (
		<Box style={{ position: 'relative' }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
			>
				<Paper
					p={{ base: 'xs', sm: 'xl' }}
					radius="md"
					miw={{ base: 'calc(100vw - 20px)', xs: 480 }}
				>
					<Stack>
						<Stack gap="sm" ta="center">
							<Logo height={40} />
							<div>
								<Title mb={4}>Email Verification</Title>
								<Text size="sm" c={colors.textMuted}>
									We&apos;ve sent a verification code to your email
								</Text>
							</div>
						</Stack>

						{error && (
							<Alert
								icon={<IconAlertCircle size={20} color={colors.error} />}
								color="error"
							>
								{error}
							</Alert>
						)}

						<Stack gap="xl" align="center">
							<PinInput
								length={6}
								type="number"
								size={isMobile ? 'md' : 'xl'}
								value={code}
								onChange={setCode}
								disabled={loading}
								onComplete={handleVerify}
								error={!!error}
							/>

							<Text size="sm" c={colors.textMuted} ta="center">
								Check your email for the 6-digit verification code
							</Text>

							{loading && (
								<Text size="sm" c={colors.textMuted}>
									Verifying...
								</Text>
							)}

							<Group justify="center" gap="xs">
								<Text size="xs" c="dimmed">
									Didn&apos;t receive the code?
								</Text>
								<Button
									variant="subtle"
									size="xs"
									onClick={handleResendCode}
									loading={isResending}
									leftSection={<IconRefresh size={14} />}
									disabled={isResending}
								>
									Resend Code
								</Button>
							</Group>
						</Stack>

						<Button
							fullWidth
							onClick={handleVerify}
							loading={loading}
							disabled={code.length !== 6}
							size="sm"
						>
							Verify
						</Button>

						<Group justify="center">
							<Anchor
								component={Link}
								to="/auth/login"
								style={{ display: 'flex', alignItems: 'center', gap: 4 }}
							>
								<IconArrowLeft size={16} />
								Back to login
							</Anchor>
						</Group>
					</Stack>
				</Paper>
			</motion.div>
		</Box>
	);
}
